import { Component, Optional, signal, AfterViewInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import { environment } from 'src/environments/environment';
import device from 'current-device';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { BehaviorSubject } from 'rxjs';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { AccountService } from 'src/app/services/account/account.service';
import { HttpService } from 'src/app/services/core/http.service';
import { CommonModule } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { ModalOptionPanelComponent } from '../../modal-option-panel/modal-option-panel.component';
import { PrivacyPolicyComponent } from '../privacy-policy/privacy-policy.component';
import { EulaComponent } from '../eula/eula.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { RecaptchaComponent } from 'src/app/components/ui-components/recaptcha/recaptcha.component';
import { EvCalculationService } from 'src/app/services/core/ev-calculation.service';

@Component({
  standalone: true,
  selector: 'app-feedback',
  templateUrl: './feedback.component.html',
  imports: [CommonModule, MatCheckbox, FormsModule, ModalOptionPanelComponent, RecaptchaComponent],
  styleUrls: ['./feedback.component.scss']
})
export class FeedbackComponent implements AfterViewInit {

  public readonly siteKey = environment.RECAPTCHA_SITE_KEY;
  public readonly rating = signal<number>(0);
  public starCount: number = 5;
  public readonly ratingArr = signal<number[]>([]);
  public readonly reviewText = signal<string>("");
  public readonly allowSensitiveData = signal<boolean>(false);
  public readonly email = signal<string | null>("");
  public readonly captchaResponse = signal<string | null>(null);
  readonly recaptchaError = signal(false);
  readonly msgError = signal(false);
  readonly emailError = signal(false);
  readonly success = signal(false);
  readonly sending = signal(false);
  readonly failed = signal(false);
  public readonly webView = signal<boolean>(false);
  public readonly mobile = signal<boolean>(false);

  public ObservableTabChangeRequest: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private readonly waypointService: WaypointService, private readonly evCalculationService: EvCalculationService, public matDialog: MatDialog, private readonly utilsService: UtilsService,
    public webviewService: WebviewService, public settingsService: SettingsService, public languageService: LanguageService,
    public accountService: AccountService, private readonly httpService: HttpService, private readonly stackedModalsService: StackedModalsService,
    private readonly vehicleService: VehicleService,
    @Optional() public dialogRef: MatDialogRef<FeedbackComponent> | null) {

    if (this.webviewService.IsGPSTWebview()) {
      this.webView.set(true);
    }
    this.ratingArr.set(Array.from({ length: this.starCount }, (_, index) => index));
    this.mobile.set(device.mobile());

    if (this.accountService.getIsAuthorized()) {
      this.email.set(this.accountService.getUserProfile().Profile.Email);
    }
  }

  ngAfterViewInit(): void {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    if (this.mobile()) {
      setTimeout(() => {
        if (document.getElementsByClassName("g-recaptcha-bubble-arrow").length > 0) {
          document.getElementsByClassName("g-recaptcha-bubble-arrow")[0].parentElement?.classList.add("recaptcha-resize");
        }
        else {
          // really slow devices
          setTimeout(() => {
            if (document.getElementsByClassName("g-recaptcha-bubble-arrow").length > 0) {
              document.getElementsByClassName("g-recaptcha-bubble-arrow")[0].parentElement?.classList.add("recaptcha-resize");
            }
          }, 3000);
        }
      }, 1000);
    }
  }

  public showIcon(index: number): boolean {
    if (this.rating() >= index + 1) {
      return true;
    } else {
      return false;
    }
  }

  public openPrivacyPolicy(): void {
    this.stackedModalsService.openModalWindow(PrivacyPolicyComponent, {
      panelClass: 'dialog-snap-to-fullscreen',
      autoFocus: false
    });
  }

  public openEula(): void {
    this.stackedModalsService.openModalWindow(EulaComponent, {
      panelClass: 'dialog-snap-to-fullscreen',
      autoFocus: false
    });
  }

  public openEUDataActPolicy(): void {
    this.utilsService.openUrlOnNewTab(device, "./assets/eu-data-policy.html",);
  }

  public accept(): void {
    if (this.failed() || this.sending() || this.success()) return;
    if (!this.validateForm()) return;

    if (this.email() == "") {
      this.email.set(null);
    }
    const userAgent = this.allowSensitiveData() ? navigator.userAgent : null;
    const inputParams = this.allowSensitiveData() ? JSON.stringify(this.evCalculationService.getRangeParams(this.vehicleService.getECarSettings()!, this.waypointService.getWaypointsParams())) : null;
    const userProfileJson = this.getUserProfileJson();

    this.sending.set(true);
    // reCAPTCHA tokens are single-use: whatever the outcome, the next send needs the newly rendered captcha solved.
    const captchaResponse = this.captchaResponse()!;
    this.captchaResponse.set(null);

    this.httpService.sendFeedbackEmail({
      captchaResponse,
      email: this.email() ?? '',
      rating: this.rating(),
      reviewText: this.reviewText(),
      userAgent,
      inputParams,
      userProfileJson
    }).subscribe({
      next: () => {
        this.sending.set(false);
        this.success.set(true);
      },
      error: () => {
        this.sending.set(false);
        this.failed.set(true);
        setTimeout(() => this.failed.set(false), 5000);
      }
    });
  }

  // Sets the per-field error flags; returns true when the form can be sent.
  private validateForm(): boolean {
    this.msgError.set(false);
    this.recaptchaError.set(false);
    this.emailError.set(false);

    if (this.captchaResponse() == null) {
      this.recaptchaError.set(true);
    }
    if (this.email() != "" && !this.utilsService.validateEmail(this.email()) || this.email() == "") {
      this.emailError.set(true);
    }
    if (this.rating() == 0 && this.reviewText() == "") {
      this.msgError.set(true);
    }
    return !this.msgError() && !this.emailError() && !this.recaptchaError();
  }

  // The signed-in user's profile without the (large) history list, or null for anonymous users.
  private getUserProfileJson(): string | null {
    if (!this.accountService.getIsAuthorized()) return null;
    const userProfile: any = { ...this.accountService.getUserProfile() };
    if (userProfile.HistoryList) {
      delete userProfile.HistoryList;
    }
    return JSON.stringify(userProfile);
  }

  public resetform(): void {
    this.sending.set(false);
    this.success.set(false);
    this.rating.set(0);
    this.email.set("");
    this.reviewText.set("");
    this.captchaResponse.set(null);
    if (this.accountService.getIsAuthorized()) {
      this.email.set(this.accountService.getUserProfile().Profile.Email);
    }
  }

  public resolved(captchaResponse: string | null): void {
    this.captchaResponse.set(captchaResponse);
  }

}
